import { SALESCHANNEL_CONSUMER } from '@vfde-sails/constants';
import { IInitialState } from 'Container/App/interface';
import {
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';
import { getDeviceDetailGroup } from 'Container/App/api/glados';
import {
    Color,
    PriceType,
} from '@vfde-sails/glados-v2';
import { IAccordionItemProperties } from '@vfde-brix/ws10/accordion';
import {
    PageViewTrackingData,
    TrackType,
} from '@vfde-sails/tracking';

/**
 * Initial state
 */
export const initialState: IInitialState = {
    isInitialLoadDone: false,
    customerConfig: {
        salesChannel: SALESCHANNEL_CONSUMER,
        deviceId: null,
        atomicId: null,
        isHardwareOnly: undefined,
        paymentMethod: undefined,
    },
    currentColor: null,
    currentCaseSize: null,
    activeAccordionPanel: null,
};

const stoneDdpSlice = createSlice({
    name: 'Stone DDP',
    initialState,
    reducers: {
        setInitialState: (state, action: PayloadAction<IInitialState['customerConfig']>) => {
            state.customerConfig = action.payload;
        },
        changeColor: (state, action: PayloadAction<Color['displayLabel']>) => {
            state.currentColor = action.payload;
        },
        changeCaseSize: (state, action: PayloadAction<string>) => {
            state.currentCaseSize = action.payload;
        },
        setAtomicId: (state, action: PayloadAction<string>) => {
            state.customerConfig.atomicId = action.payload;
        },
        setHardwareOnly: (state, action: PayloadAction<boolean>) => {
            state.customerConfig.isHardwareOnly = action.payload;
        },
        setPaymentMethod: (state, action: PayloadAction<PriceType>) => {
            state.customerConfig.paymentMethod = action.payload;
        },
        updateStorage: () => {},
        goToBasket: () => {},
        toggleAccordionItem: (state, action: PayloadAction<Pick<IAccordionItemProperties, 'stdId' | 'optOpen' | 'stdHeadline'>>) => {
            state.activeAccordionPanel = action.payload.optOpen ? action.payload.stdId : null;
        },
        triggerTracking: {
            reducer: () => {},
            prepare: (
                eventType: TrackType,
                eventPayload: PageViewTrackingData,
            ) => ({
                payload: {
                    trackingData: {
                        eventType,
                        eventPayload,
                    },
                },
            }),
        },
    },
    extraReducers: builder => {
        builder
            .addMatcher(
                action => getDeviceDetailGroup.matchFulfilled(action),
                (state, action) => {
                    const { atomics } = action.payload;

                    if (
                        !state.customerConfig?.atomicId
                        || !atomics.some(item => item.atomicId === state.customerConfig?.atomicId)
                    ) {
                        state.customerConfig.atomicId = atomics.find(item => item.isDefault)?.atomicId || null;
                    }

                    state.currentColor = atomics
                        .find(item => item.atomicId === state.customerConfig.atomicId)?.color.displayLabel || null;
                    state.currentCaseSize = atomics
                        .find(item => item.atomicId === state.customerConfig.atomicId)?.caseSize || null;
                },
            )
        ;
    },
    selectors: {
        selectCustomerConfig: (state): IInitialState['customerConfig'] => state.customerConfig,
        selectCurrentColor: (state): IInitialState['currentColor'] => state.currentColor,
        selectCurrentCaseSize: (state): IInitialState['currentCaseSize'] => state.currentCaseSize,
        selectActiveAccordionPanel: (state): IInitialState['activeAccordionPanel'] => state.activeAccordionPanel,
    },
});

export default stoneDdpSlice;

export const {
    setInitialState,
    updateStorage,
    changeColor,
    changeCaseSize,
    setAtomicId,
    setHardwareOnly,
    setPaymentMethod,
    goToBasket,
    triggerTracking,
    toggleAccordionItem,
} = stoneDdpSlice.actions;

/**
 * Action Dispatchers
 */
export type ActionDispatchers = Pick<
    typeof stoneDdpSlice.actions,
    'setInitialState' | 'updateStorage' | 'changeColor' | 'changeCaseSize' | 'setHardwareOnly' | 'setPaymentMethod' | 'goToBasket' | 'toggleAccordionItem'
>;

export const {
    selectCustomerConfig,
    selectCurrentCaseSize,
    selectCurrentColor,
    selectActiveAccordionPanel,
} = stoneDdpSlice.selectors;
